import React, { useState, useEffect } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, InputAdornment, 
    List, ListItem, ListItemAvatar, ListItemText, Avatar, Typography, Box, Chip, IconButton, 
    CircularProgress, Paper, Grid, Divider
} from '@mui/material';
import {
    Search as SearchIcon, Person as PersonIcon, Add as AddIcon, Close as CloseIcon, 
    Phone as PhoneIcon, Email as EmailIcon, Group as GroupIcon, CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import axios from 'axios';
import styled from 'styled-components';

const getBaseUrl = () => {
    const baseUrl = process.env.REACT_APP_BASE_URL || '';
    return baseUrl.replace(/\/+$/, '');
};

const ParentChooser = ({
    open, onClose, onSelect, onCreateNew, schoolId,
    title = "Select Parent / Guardian",
    buttonText = "Select Parent",
    selectedParent = null,
    showCreateNew = true
}) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [parents, setParents] = useState([]);
    const [loading, setLoading] = useState(false);
    const [filteredParents, setFilteredParents] = useState([]);

    useEffect(() => {
        if (open && schoolId) fetchParents();
    }, [open, schoolId]);

    useEffect(() => {
        if (parents.length > 0) {
            const search = searchTerm.toLowerCase();
            const filtered = parents.filter(p =>
                p.fatherName?.toLowerCase().includes(search) ||
                p.motherName?.toLowerCase().includes(search) ||
                p.email?.toLowerCase().includes(search) ||
                p.phone?.includes(search) ||
                p.fatherPhone?.includes(search)
            );
            setFilteredParents(filtered);
        }
    }, [searchTerm, parents]);

    const fetchParents = async () => {
        setLoading(true);
        try {
            const result = await axios.get(`${getBaseUrl()}/Parents/${schoolId}`);
            if (result.data && Array.isArray(result.data)) {
                setParents(result.data);
                setFilteredParents(result.data);
            }
        } catch (error) {
            console.error("Error fetching parents:", error);
            setParents([]);
        }
        setLoading(false);
    };

    const getParentDisplayName = (p) => p.fatherName || p.motherName || p.guardianName || 'Unknown';
    const getParentPhone = (p) => p.phone || p.fatherPhone || p.motherPhone || 'N/A';

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{ sx: { borderRadius: '24px', overflow: 'hidden' } }}
        >
            <StyledDialogTitle>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }}><GroupIcon /></Avatar>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>{title}</Typography>
                </Box>
                <IconButton onClick={onClose} sx={{ color: 'white' }}><CloseIcon /></IconButton>
            </StyledDialogTitle>

            <DialogContent sx={{ p: 3 }}>
                <TextField
                    fullWidth
                    placeholder="Search by name, email, or phone..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon sx={{ color: '#667eea' }} />
                            </InputAdornment>
                        ),
                        sx: { borderRadius: '12px', bgcolor: '#f8fafc' }
                    }}
                    sx={{ mb: 3, mt: 1 }}
                    size="small"
                />

                {showCreateNew && (
                    <Button
                        variant="outlined"
                        startIcon={<AddIcon />}
                        onClick={() => { onCreateNew(); onClose(); }}
                        fullWidth
                        sx={{ mb: 3, borderRadius: '12px', textTransform: 'none', fontWeight: 600, borderStyle: 'dashed' }}
                    >
                        Create New Parent Profile
                    </Button>
                )}

                <Divider sx={{ mb: 2 }}><Typography variant="caption" color="textSecondary">RESULTS</Typography></Divider>

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={30} /></Box>
                ) : filteredParents.length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 6 }}>
                        <PersonIcon sx={{ fontSize: 64, color: '#e2e8f0', mb: 1 }} />
                        <Typography color="textSecondary">No parents found</Typography>
                    </Box>
                ) : (
                    <List sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        {filteredParents.map((parent) => {
                            const isSelected = selectedParent?._id === parent._id;
                            return (
                                <ParentItemCard
                                    key={parent._id}
                                    elevation={0}
                                    selected={isSelected}
                                    onClick={() => { onSelect(parent); onClose(); }}
                                >
                                    <ListItem 
                                        sx={{ p: 1.5 }}
                                        secondaryAction={isSelected && <CheckCircleIcon color="primary" />}
                                    >
                                        <ListItemAvatar>
                                            <Avatar 
                                                src={parent.photo ? process.env.REACT_APP_BASE_URL`/${parent.photo}` : ''}
                                                sx={{ bgcolor: '#667eea', width: 48, height: 48 }}
                                            >
                                                <PersonIcon />
                                            </Avatar>
                                        </ListItemAvatar>
                                        <ListItemText
                                            primary={
                                                <Typography sx={{ fontWeight: 700, color: isSelected ? '#667eea' : '#1a1a2e' }}>
                                                    {getParentDisplayName(parent)}
                                                </Typography>
                                            }
                                            secondary={
                                                <Box sx={{ display: 'flex', flexDirection: 'column', mt: 0.5 }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                        <PhoneIcon sx={{ fontSize: 12, color: '#64748b' }} />
                                                        <Typography variant="caption">{getParentPhone(parent)}</Typography>
                                                    </Box>
                                                    {parent.email && (
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                            <EmailIcon sx={{ fontSize: 12, color: '#64748b' }} />
                                                            <Typography variant="caption">{parent.email}</Typography>
                                                        </Box>
                                                    )}
                                                </Box>
                                            }
                                        />
                                    </ListItem>
                                </ParentItemCard>
                            );
                        })}
                    </List>
                )}
            </DialogContent>

            <DialogActions sx={{ p: 2.5, bgcolor: '#f8fafc' }}>
                <Button onClick={onClose} sx={{ color: '#64748b', fontWeight: 600 }}>Cancel</Button>
            </DialogActions>
        </Dialog>
    );
};

export default ParentChooser;

const StyledDialogTitle = styled(DialogTitle)`
    background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%) !important;
    color: white !important;
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 20px 24px !important;
`;

const ParentItemCard = styled(Paper)`
    border-radius: 16px !important;
    border: 1px solid ${props => props.selected ? '#667eea' : '#e2e8f0'} !important;
    background: ${props => props.selected ? '#f0f4ff' : 'white'} !important;
    transition: all 0.2s ease;
    cursor: pointer;
    
    &:hover {
        border-color: #667eea !important;
        background: #f0f4ff !important;
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(102, 126, 234, 0.1) !important;
    }
`;
